<div class="task-attachments-container"
  (dragenter)="!isLocked && onDragEnter($event)"
  (dragover)="!isLocked && onDragOver($event)"
  (dragleave)="!isLocked && onDragLeave($event)"
  (drop)="!isLocked && onDrop($event)"
  [ngClass]="{'permission-disabled-drag': isLocked}">

  <!-- DRAG & DROP OVERLAY (matching document manager) -->
  @if (isDragging && !isLocked) {
    <div class="drag-overlay" @slideInFromTop>
      <div class="upload-indicator">
        <!-- File count badge -->
        @if (draggedFileCount > 0) {
          <div class="file-count-badge">
            {{ draggedFileCount }} file{{ draggedFileCount > 1 ? 's' : '' }}
          </div>
        }
        <div class="upload-icon-circle">
          <i class="fas fa-cloud-upload-alt upload-icon"></i>
        </div>
        <h3 class="upload-title">Drop files to upload</h3>
        <p class="upload-subtitle">Release to upload {{ draggedFileCount }} file(s)</p>
      </div>
    </div>
  }

  <!-- HEADER ACTIONS (upload button + view toggle) -->
  <div class="upload-actions">
    <input type="file"
      #fileInput
      multiple
      (change)="onFileSelected($event)"
      style="display: none"
      [disabled]="isLocked">
    <button type="button"
      class="btn-upload-small"
      (click)="!isLocked && fileInput.click()"
      [disabled]="isLocked"
      [ngClass]="{'permission-disabled': isLocked}">
      <i class="fas fa-upload"></i>
      <span>Upload</span>
    </button>

    <!-- View Toggle -->
    <div class="view-toggle" role="group" aria-label="View mode options">
      <button class="view-toggle-btn"
        [class.active]="viewMode === 'grid'"
        (click)="setViewMode('grid')"
        [attr.aria-pressed]="viewMode === 'grid'"
        title="Grid view">
        <i class="fas fa-th"></i>
      </button>
      <button class="view-toggle-btn"
        [class.active]="viewMode === 'list'"
        (click)="setViewMode('list')"
        [attr.aria-pressed]="viewMode === 'list'"
        title="List view">
        <i class="fas fa-list"></i>
      </button>
    </div>
  </div>

  <!-- FILES GRID VIEW -->
  @if (documents.length > 0 && viewMode === 'grid') {
    <div class="files-grid">
      @for (doc of documents; track doc) {
        <div class="file-card" @zoomIn>
          <!-- File Preview/Icon -->
          <div class="file-preview" (click)="openPreview(doc)" style="cursor: pointer;">
            <!-- Show actual image if it's an image file -->
            @if (getImageIcon(doc)) {
              <img [src]="getImageIcon(doc)"
                [alt]="doc.original_name || doc.name"
                class="file-thumbnail"
                (error)="onImageError($event)">
              <i class="fa {{ getFileIcon(doc) }}"
                [ngClass]="'file-icon-' + getFileTypeColor(doc)"
              style="display: none;"></i>
            } @else {
              <i class="fa {{ getFileIcon(doc) }}"
              [ngClass]="'file-icon-' + getFileTypeColor(doc)"></i>
            }
          </div>
          <!-- File Info -->
          <div class="file-details">
            <div class="file-name" [title]="doc.original_name || doc.name">
              {{ doc.original_name || doc.name }}
            </div>
            <div class="file-meta">
              <span class="file-size">{{ doc.file_size ? doc.file_size + ' KB' : formatFileSize(doc.size || 0) }}</span>
              <span class="file-ext">{{ (doc.file_type || doc.extension || '').toUpperCase() }}</span>
            </div>
          </div>
          <!-- Actions Overlay (visible on hover) -->
          <div class="file-actions-overlay">
            <button type="button"
              class="btn-action-overlay"
              (click)="openPreview(doc)"
              title="Preview file">
              <i class="fas fa-eye"></i>
            </button>
            <button type="button"
              class="btn-action-overlay"
              (click)="downloadFile(doc)"
              title="Download file">
              <i class="fas fa-download"></i>
            </button>
            @if (!isLocked) {
              <button type="button"
                class="btn-action-overlay btn-delete-overlay"
                (click)="!isLocked && confirmDelete(doc)"
                title="Delete file"
                [disabled]="isLocked"
                [ngClass]="{'permission-disabled': isLocked}"
                >
                <i class="fas fa-trash"></i>
              </button>
            }
          </div>
        </div>
      }
    </div>
  }

  <!-- FILES LIST VIEW -->
  @if (documents.length > 0 && viewMode === 'list') {
    <div class="files-table" @zoomIn>
      <table class="table table-hover">
        <thead>
          <tr>
            <th style="width: 40px;"></th>
            <th>Name</th>
            <th style="width: 100px;">Type</th>
            <th style="width: 100px;">Size</th>
            <th style="width: 120px;">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (doc of documents; track doc) {
            <tr (click)="openPreview(doc)" style="cursor: pointer;">
              <td (click)="$event.stopPropagation()">
                <i class="fa {{ getFileIcon(doc) }}"
                [ngClass]="'file-icon-' + getFileTypeColor(doc)"></i>
              </td>
              <td class="file-name-cell">
                <div class="file-name-text" [title]="doc.original_name || doc.name">
                  {{ doc.original_name || doc.name }}
                </div>
              </td>
              <td>
                <span class="file-type-badge">{{ (doc.file_type || doc.extension || '').toUpperCase() }}</span>
              </td>
              <td class="file-size-cell">
                {{ doc.file_size ? doc.file_size + ' KB' : formatFileSize(doc.size || 0) }}
              </td>
              <td (click)="$event.stopPropagation()">
                <button type="button"
                  class="btn-icon-small"
                  (click)="openPreview(doc)"
                  title="Preview file">
                  <i class="fas fa-eye"></i>
                </button>
                <button type="button"
                  class="btn-icon-small"
                  (click)="downloadFile(doc)"
                  title="Download file">
                  <i class="fas fa-download"></i>
                </button>
                <button type="button"
                  class="btn-icon-small btn-delete"
                  (click)="confirmDelete(doc)"
                  title="Delete file">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }

  <!-- EMPTY STATE -->
  @if (documents.length === 0 && !isLoading) {
    <div class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-cloud-upload-alt"></i>
      </div>
      <h4>No attachments yet</h4>
      <p>Drag and drop files here or click upload button</p>
    </div>
  }

  <!-- LOADING STATE -->
  @if (isLoading) {
    <div class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading attachments...</p>
    </div>
  }

</div>

<!-- UPLOAD PROGRESS PANEL (matching document manager) -->
@if (uploadQueue.length > 0) {
  <div class="upload-progress-panel" @slideInFromTop>
    <!-- Panel Header -->
    <div class="upload-panel-header">
      <div class="header-info">
        <i class="fas fa-upload"></i>
        <span class="upload-count">
          Uploading {{ getUploadingCount() }} of {{ uploadQueue.length }} files
        </span>
      </div>
      <div class="header-actions">
        <button type="button"
          class="btn-icon"
          (click)="toggleUploadPanel()">
          <i class="fas" [ngClass]="isUploadPanelOpen ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </button>
        @if (hasCompletedUploads()) {
          <button
            type="button"
            class="btn-icon"
            (click)="clearCompleted()">
            <i class="fas fa-check"></i>
          </button>
        }
        <button type="button"
          class="btn-icon"
          (click)="clearAll()">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
    <!-- File List -->
    @if (isUploadPanelOpen) {
      <div class="upload-file-list">
        @for (file of uploadProgress; track file) {
          <div
            class="upload-file-item"
         [ngClass]="{
           'upload-status-pending': file.status === 'pending',
           'upload-status-uploading': file.status === 'uploading',
           'upload-status-complete': file.status === 'complete',
           'upload-status-error': file.status === 'error',
           'upload-status-cancelled': file.status === 'cancelled'
         }">
            <!-- File Preview -->
            <div class="upload-file-preview">
              @if (file.preview) {
                <img [src]="file.preview" [alt]="file.fileName">
              }
              @if (!file.preview) {
                <i class="fas fa-file upload-file-icon"></i>
              }
            </div>
            <!-- File Info -->
            <div class="upload-file-info">
              <div class="upload-file-name" [title]="file.fileName">{{ file.fileName }}</div>
              <div class="upload-file-details">
                <span class="upload-file-size">{{ file.fileSize }}</span>
                <!-- Status-specific details -->
                @switch (file.status) {
                  <!-- Pending -->
                  @case ('pending') {
                    <span class="upload-file-status">
                      <i class="fas fa-clock"></i>Waiting...
                    </span>
                  }
                  <!-- Uploading -->
                  @case ('uploading') {
                    <span class="upload-file-status">
                      <i class="fas fa-spinner fa-spin"></i>
                      {{ file.progress }}% • {{ formatUploadSpeed(file.uploadSpeed || 0) }}
                      @if (file.timeRemaining) {
                        <span> • {{ formatTimeRemaining(file.timeRemaining) }}</span>
                      }
                    </span>
                  }
                  <!-- Complete -->
                  @case ('complete') {
                    <span class="upload-file-status upload-status-success">
                      <i class="fas fa-check-circle"></i>Complete
                    </span>
                  }
                  <!-- Error -->
                  @case ('error') {
                    <span class="upload-file-status upload-status-error">
                      <i class="fas fa-exclamation-circle"></i>{{ file.error || 'Failed' }}
                    </span>
                  }
                  <!-- Cancelled -->
                  @case ('cancelled') {
                    <span class="upload-file-status upload-status-cancelled">
                      <i class="fas fa-ban"></i>Cancelled
                    </span>
                  }
                }
              </div>
              <!-- Progress Bar (only for uploading) -->
              @if (file.status === 'uploading') {
                <div class="upload-file-progress">
                  <div class="progress">
                    <div class="progress-bar" [style.width.%]="file.progress"></div>
                  </div>
                </div>
              }
            </div>
            <!-- File Actions -->
            <div class="upload-file-actions">
              @if (file.status === 'uploading') {
                <button
                  class="btn-icon btn-icon-danger"
                  (click)="cancelUpload(file)">
                  <i class="fas fa-times"></i>
                </button>
              }
              @if (file.status === 'error') {
                <button
                  class="btn-icon btn-icon-primary"
                  (click)="retryUpload(file)">
                  <i class="fas fa-redo"></i>
                </button>
              }
              @if (file.status === 'complete' || file.status === 'error' || file.status === 'cancelled') {
                <button
                  class="btn-icon"
                  (click)="removeFromQueue(file)">
                  <i class="fas fa-trash"></i>
                </button>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>
}

<!-- DELETE CONFIRMATION MODAL - Now using shared ConfirmationPopupComponent -->
